'use client';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Step1PhotoUpload from './Step1PhotoUpload';
import Step2LeverageQuestions from './Step2LeverageQuestions';
import Step3ContactInfo from './Step3ContactInfo';
import EstimateReveal from './EstimateReveal';
import QuoteHeader from './QuoteHeader';
import QuoteProgressBar from './QuoteProgressBar';
import { createClient } from '@/lib/supabase/client';

export interface QuoteFormData {
  photo: File | null;
  photoPreviewUrl: string;
  projectType: string;
  budgetRange: string;
  timeline: string;
  priority: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

export interface EstimateResult {
  roughEstimate: { min: number; max: number };
  leverageScore: 'High' | 'Medium' | 'Low';
  scoreReason: string;
  projectType: string;
  confidence: number;
  breakdown?: {
    baseFee: number;
    perUnitRate: number;
    estimatedUnits: number;
    unit: string;
    subtotal: number;
    variance: number;
    appliedRule: string;
  };
  scoreFactors?: {
    budgetSignal: 'strong' | 'moderate' | 'weak';
    urgencySignal: 'high' | 'medium' | 'low';
    intentSignal: 'committed' | 'conditional' | 'browsing';
    photoQuality: 'good' | 'acceptable' | 'missing';
  };
}

const defaultValues: QuoteFormData = {
  photo: null,
  photoPreviewUrl: '',
  projectType: '',
  budgetRange: '',
  timeline: '',
  priority: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
};

export default function QuickQuoteWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimate, setEstimate] = useState<EstimateResult | null>(null);
  const totalSteps = 3;

  const form = useForm<QuoteFormData>({ defaultValues, mode: 'onBlur' });

  const handleNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, totalSteps));
  }, []);

  const handleBack = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 1));
  }, []);

  const handleSubmit = useCallback(async (data: QuoteFormData) => {
    setIsSubmitting(true);
    try {
      // Build photo metadata and base64 from the uploaded File object
      let photoMetadata: {
        width?: number;
        height?: number;
        fileSize?: number;
        fileName?: string;
        mimeType?: string;
      } | undefined;
      let photoBase64: string | undefined;

      if (data.photo) {
        photoMetadata = {
          fileSize: data.photo.size,
          fileName: data.photo.name,
          mimeType: data.photo.type,
        };

        // Convert photo to base64 data URI for OpenAI Vision
        photoBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(data.photo as File);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
        });

        // Extract image dimensions via a temporary Image element
        if (data.photoPreviewUrl) {
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
              if (photoMetadata) {
                photoMetadata.width = img.naturalWidth;
                photoMetadata.height = img.naturalHeight;
              }
              resolve();
            };
            img.onerror = () => resolve();
            img.src = data.photoPreviewUrl;
          });
        }
      }

      // Call the backend calculation API (with Vision analysis when photo present)
      const response = await fetch('/api/calculate-estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectType: data.projectType,
          budgetRange: data.budgetRange,
          timeline: data.timeline,
          priority: data.priority,
          photoBase64,   // Full data URI — triggers OpenAI Vision on the server
          photoMetadata, // Kept as fallback metadata
        }),
      });

      if (!response.ok) {
        throw new Error(`Estimate API returned ${response.status}`);
      }

      const result: EstimateResult = await response.json();
      setEstimate(result);

      // Persist lead + score to Supabase
      try {
        const supabase = createClient();
        const { error: dbError } = await supabase.from('scored_leads').insert({
          // Contact info
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          phone: data.phone || null,
          address: data.address || null,
          notes: data.notes || null,
          // Project info
          project_type: data.projectType,
          budget_range: data.budgetRange,
          timeline: data.timeline,
          priority: data.priority,
          // Estimate results
          estimate_min: result.roughEstimate.min,
          estimate_max: result.roughEstimate.max,
          leverage_score: result.leverageScore,
          score_reason: result.scoreReason,
          confidence: result.confidence,
          // Score factors
          budget_signal: result.scoreFactors?.budgetSignal ?? 'weak',
          urgency_signal: result.scoreFactors?.urgencySignal ?? 'low',
          intent_signal: result.scoreFactors?.intentSignal ?? 'browsing',
          photo_quality: result.scoreFactors?.photoQuality ?? 'missing',
          // Breakdown
          breakdown: result.breakdown ?? null,
          // Photo metadata
          photo_file_name: photoMetadata?.fileName ?? null,
          photo_mime_type: photoMetadata?.mimeType ?? null,
          photo_file_size: photoMetadata?.fileSize ?? null,
        });

        if (dbError) {
          console.error('[QuickQuoteWizard] Supabase insert error:', dbError.message);
        }
      } catch (dbErr) {
        console.error('[QuickQuoteWizard] Failed to persist lead:', dbErr);
      }

      toast.success('Quote submitted! Your estimate is ready.');
    } catch (err) {
      console.error('[QuickQuoteWizard] estimate error:', err);
      toast.error('Could not generate estimate. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  if (estimate) {
    return <EstimateReveal estimate={estimate} formData={form.getValues()} onNewQuote={() => { setEstimate(null); setCurrentStep(1); form.reset(defaultValues); }} />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <QuoteHeader />
      <div className="flex-1 flex flex-col items-center justify-start px-4 py-6 pb-8">
        <div className="w-full max-w-lg">
          <QuoteProgressBar currentStep={currentStep} totalSteps={totalSteps} />

          <div className="mt-6 animate-slide-up">
            {currentStep === 1 && (
              <Step1PhotoUpload form={form} onNext={handleNext} />
            )}
            {currentStep === 2 && (
              <Step2LeverageQuestions form={form} onNext={handleNext} onBack={handleBack} />
            )}
            {currentStep === 3 && (
              <Step3ContactInfo
                form={form}
                onBack={handleBack}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>
      </div>

      {/* Powered by footer */}
      <footer className="py-4 text-center border-t border-[#1A1A1A]">
        <p className="text-[11px] text-[#444] font-500 tracking-wide">
          Powered by{' '}
          <a href="https://quotes.seriousleverage.com" target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-blue-400 transition-colors">
            Serious Quotes
          </a>
          {' '}— AI Lead Qualification
        </p>
      </footer>
    </div>
  );
}